import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sectionForPath, type SubItem } from './nav-config';
import { useAuthStore } from '@/shared/store/authStore';
import { api } from '@/shared/api/client';
import { zoomOut } from './HistoryBridge';
import { useZoomStore } from '@/shared/store/zoomStore';
import { useOrderedSubItems, useNavPrefs } from '@/shared/store/navPrefsStore';
import { IconChevron, IconGrip } from '@/components/ui/Icon';
import { FavoriteStar } from '@/features/favorites/FavoriteStar';
import { formatTemp, useTempUnit } from '@/features/home/tempUnit';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { usePriorityOverflow, isShown } from '@/shared/hooks/usePriorityOverflow';

// The Home section shows a personalized greeting in place of section tabs.
function greetingText(name?: string) {
  const h = new Date().getHours();
  const g = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  const first = (name || '').trim().split(/\s+/)[0] || 'there';
  return `${g}, ${first}`;
}

// met.no symbol_code -> short human text ("partlycloudy_day" -> "Partly cloudy").
const WEATHER_TEXT: Record<string, string> = {
  clearsky: 'Clear', fair: 'Fair', partlycloudy: 'Partly cloudy', cloudy: 'Cloudy',
  fog: 'Fog', rain: 'Rain', lightrain: 'Light rain', heavyrain: 'Heavy rain',
  rainshowers: 'Rain showers', lightrainshowers: 'Light showers', heavyrainshowers: 'Heavy showers',
  drizzle: 'Drizzle', snow: 'Snow', lightsnow: 'Light snow', heavysnow: 'Heavy snow',
  snowshowers: 'Snow showers', sleet: 'Sleet', thunderstorm: 'Thunderstorm',
};
function weatherText(symbol: string | null): string | null {
  if (!symbol) return null;
  const base = symbol.split('_')[0];
  if (WEATHER_TEXT[base]) return WEATHER_TEXT[base];
  const cleaned = base.replace(/andthunder$/, '').replace(/showers$/, ' showers');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// Date + (optional) local weather beside the greeting. Weather asks for the
// browser's coarse position once; declined -> just the date. Data proxied
// through /api/v1/weather (MET Norway, attribution in the tooltip), always in
// Celsius; shown in the user's Home temperature unit (Customize, °F by default),
// which also follows the unit being picked before Save.
function GreetingExtras() {
  const [weather, setWeather] = useState<{ temperature_c: number | null; symbol: string | null; attribution?: string } | null>(null);
  const unit = useTempUnit();
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void api.get<{ temperature_c: number | null; symbol: string | null; attribution?: string }>(
          `/weather?lat=${pos.coords.latitude.toFixed(2)}&lon=${pos.coords.longitude.toFixed(2)}`)
          .then(setWeather).catch(() => {});
      },
      () => { /* declined — date only */ },
      { maximumAge: 30 * 60 * 1000, timeout: 8000 },
    );
  }, []);
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const cond = weatherText(weather?.symbol ?? null);
  return (
    <span className="text-[0.75rem] text-muted font-normal whitespace-nowrap">
      <span className="mx-2 text-border-cool">·</span>{dateStr}
      {weather?.temperature_c != null && unit && (
        <span title={weather.attribution || 'Weather data from MET Norway'}>
          <span className="mx-2 text-border-cool">·</span>
          {formatTemp(weather.temperature_c, unit)}{cond ? ` ${cond}` : ''}
        </span>
      )}
    </span>
  );
}

// Second-level pill geometry, shared by the visible pills, the hidden
// measuring row and the "More" pill so all three size alike. The keyboard ring
// is 2px and focus-visible only (a click never leaves one): white on the teal
// active pill, teal on the others.
const pillClass = (active: boolean) =>
  `px-2.5 py-0.5 rounded-full text-[0.75rem] tracking-tight whitespace-nowrap inline-flex items-center gap-1 border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
    active
      ? 'bg-primary text-white font-semibold border-primary shadow-sm focus-visible:ring-white'
      : 'text-slate-900 hover:text-primary-text font-normal border-transparent hover:bg-slate-200 focus-visible:ring-primary'
  }`;

export function SubNav() {
  const location = useLocation();
  const nav = useNavigate();
  const section = sectionForPath(location.pathname);
  const me = useAuthStore((s) => s.user);
  const company = useAuthStore((s) => s.company);
  const stack = useZoomStore((s) => s.stack);
  const currentLabel = useZoomStore((s) => s.currentLabel);
  const designing = useNavPrefs((s) => s.designing);
  const setSubOrder = useNavPrefs((s) => s.setSubOrder);
  const [dragTo, setDragTo] = useState<string | null>(null);
  const [overTo, setOverTo] = useState<string | null>(null);

  // Esc pops one zoom level, but only an Escape nothing else wanted: a field
  // keeps its native Esc; an open menu, list, popover or modal closes first
  // (it marks the key with preventDefault, or is open around the focus, or is
  // an aria-modal dialog anywhere on the page). Focus on an open popup's
  // trigger counts as inside it; an expanded disclosure (a collapsible Card
  // header, aria-expanded with no aria-haspopup) does not, so Escape there
  // still goes back.
  useEffect(() => {
    if (stack.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (t?.closest?.('[role="dialog"],[role="menu"],[role="listbox"],[aria-haspopup][aria-expanded="true"]')) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      zoomOut(stack.length - 1, nav);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stack, nav]);

  // Apply per-user ordering + hidden filter, then still enforce role gating
  // and the company flags (Receivables > QuickBooks needs a connection).
  const ordered = useOrderedSubItems(section?.key);
  const items = ordered.filter((it) => (!it.requiresRole || (me?.roles || []).includes(it.requiresRole))
    && (!it.requiresCompanyFlag || company?.[it.requiresCompanyFlag] === true));
  const moveSub = (from: string, to: string) => {
    if (!from || from === to || !section) return;
    const tos = items.map((it) => it.to);
    const fi = tos.indexOf(from); const ti = tos.indexOf(to);
    if (fi < 0 || ti < 0) return;
    tos.splice(fi, 1); tos.splice(ti, 0, from);
    setSubOrder(section.key, tos);
  };

  // Active sub-tab (and its active third-level child, if any) by longest-prefix
  // match across the tabs AND their children. Children win ties so the Overview
  // child (same path as its parent) highlights on the parent's own URL.
  const subPath = location.pathname;
  const subScore = (to: string) => (subPath === to ? to.length : subPath.startsWith(to + '/') ? to.length : -1);
  let activeParent = null as typeof items[number] | null;
  let activeChild: SubItem | null = null;
  let bestScore = -1;
  for (const it of items) {
    for (const ch of (it.children || [])) {
      const cs = subScore(ch.to);
      if (cs > bestScore) { bestScore = cs; activeParent = it; activeChild = ch; }
    }
    const s = subScore(it.to);
    if (s > bestScore) { bestScore = s; activeParent = it; activeChild = null; }
  }
  const thirdLevel = stack.length === 0 ? (activeParent?.children || []) : [];

  // Priority+ for the pills (nav-04): the ones that do not fit go into "More"
  // instead of an invisible sideways scroller. Not in crumbs, Home's greeting
  // or menu-design mode.
  const pillsMode = stack.length === 0 && section?.key !== 'home' && !designing && items.length > 0;
  const rowRef = useRef<HTMLElement | null>(null);
  const pillMeasureRef = useRef<HTMLDivElement | null>(null);
  const tailRef = useRef<HTMLSpanElement | null>(null);
  // The active pill is pinned: it never moves into More, so arrowing onto a
  // longer-labelled tab cannot push it out and drop focus.
  const activeIndex = activeParent ? items.indexOf(activeParent) : -1;
  const visibleCount = usePriorityOverflow({
    containerRef: rowRef, measureRef: pillMeasureRef, count: items.length, gap: 4, activeIndex,
    enabled: pillsMode,
    reserve: () => tailRef.current?.offsetWidth ?? 0,
    deps: [items.map((it) => `${it.to}:${it.label}`).join('|'), activeParent?.to, currentLabel],
  });

  const showSubNav = items.length > 0 || stack.length > 0;
  if (!showSubNav) return null;

  // z-40 (matching MainNav, not z-10) so this bar's own stacking context — and
  // the action menus portaled into it (Print/Download PDF, etc.) — sit ABOVE
  // the docked AI Companion panel (z-30). At z-10 the menus were trapped below.
  return (
    <div className="sticky top-[calc(30px+var(--container-gap))] [@media(pointer:coarse)]:top-[calc(48px+var(--container-gap))] z-40 bg-canvas pt-cg pb-cg">
      {/* The bar is an inset, rounded "pill" floating on the page canvas
          instead of a flush full-width strip. The sticky wrapper keeps an
          opaque bg-canvas so page content scrolling underneath is masked
          in the side gaps. */}
      {/* pl-3 lines the first sub-nav tab up with the first top-nav tab: the
          top bar starts its tabs at pl-3 since the brand mark left it
          (2026-11-03; it is on the bottom bar now). */}
      <div className="mx-2.5 pl-3 pr-1.5 h-[30px] [@media(pointer:coarse)]:h-12 rounded-xl border border-[#b8c4d6] bg-nav">
        {/* 3-zone grid: LEFT — crumbs (zoomed) or section tabs (default).
                        CENTER — current record title (always, when set).
                        RIGHT — Esc hint while zoomed, else empty.
            The wrapping row is locked to 28px tall so it matches the
            MainNav row, regardless of whether the right-hand portal
            slot is rendering page-action pills. */}
        <div className="grid items-center text-[0.75rem] h-full" style={{ gridTemplateColumns: '1fr auto' }}>
          <nav
            data-subnav
            ref={rowRef}
            aria-label={stack.length > 0 ? 'Breadcrumb' : 'Section navigation'}
            className={`relative flex items-center gap-[4px] text-secondary flex-nowrap min-w-0 whitespace-nowrap px-1 -mx-1 py-1 -my-1 ${stack.length > 0 || designing ? 'overflow-x-auto' : 'overflow-visible'}`}
            onKeyDown={(e) => {
              // ←/→ move between sub-nav tabs (or breadcrumb steps).
              // ↑ sends focus back up to the active main-nav tab.
              // ↓ sends focus down into the page content (toolbar/table).
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
              // Keys inside the open More menu belong to the menu.
              if ((e.target as HTMLElement).closest('[role="menu"]')) return;
              const links = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('a[href], button'))
                .filter((el) => !el.closest('[role="menu"]'));
              if (links.length === 0) return;
              const idx = links.indexOf(document.activeElement as HTMLElement);
              if (idx < 0) return;
              e.preventDefault();
              if (e.key === 'ArrowUp') {
                // The ACTIVE SECTION tab (Payables when on Requisitions) — not
                // aria-current, which NavLink only sets on an exact URL match,
                // so it used to fall back to the first tab (Home).
                const main = document.querySelector<HTMLElement>('[data-mainnav] a[data-active-section]')
                  || document.querySelector<HTMLElement>('[data-mainnav] [data-navtab][data-active-section]')
                  || document.querySelector<HTMLElement>('[data-mainnav] [data-navtab]');
                main?.focus();
                return;
              }
              if (e.key === 'ArrowDown') {
                // A third-level tab row (when present) comes before the page.
                const third = document.querySelector<HTMLElement>('[data-thirdnav] [data-thirdtab][data-active]')
                  || document.querySelector<HTMLElement>('[data-thirdnav] [data-thirdtab]');
                if (third) { third.focus(); return; }
                // Step into the page, top-down: the toolbar (+ New, search,
                // filter chips) comes first so the user lands on the "+"
                // button and can arrow across the filters, then the table,
                // then any focusable element in <main>.
                const target =
                  document.querySelector<HTMLElement>('main [role="toolbar"] [tabindex="0"]')
                  || document.querySelector<HTMLElement>(
                    'main [role="toolbar"] button:not([disabled]), main [role="toolbar"] a[href], main [role="toolbar"] input:not([type="hidden"]):not([disabled]), main [role="toolbar"] select:not([disabled])'
                  )
                  || document.querySelector<HTMLElement>('main [role="gridcell"][tabindex="0"]')
                  || document.querySelector<HTMLElement>('main [role="gridcell"]')
                  || document.querySelector<HTMLElement>(
                    'main button:not([disabled]), main a[href], main input:not([type="hidden"]):not([disabled]), main select:not([disabled]), main textarea:not([disabled])'
                  );
                target?.focus();
                return;
              }
              let next = idx;
              // No wrap — Left at the first item and Right at the last
              // are no-ops, so the arrow sequence never spills past the
              // edge into nearby header elements (logo / user badge).
              if (e.key === 'ArrowRight') next = Math.min(idx + 1, links.length - 1);
              else if (e.key === 'ArrowLeft') next = Math.max(idx - 1, 0);
              else if (e.key === 'Home') next = 0;
              else if (e.key === 'End') next = links.length - 1;
              if (next === idx) return;
              const target = links[next];
              if (target) {
                target.focus();
                // Auto-activate sideways moves via React Router's
                // navigate() rather than a synthetic click() — avoids any
                // browser-specific focus reset on click-to-navigate.
                // Tabs are <button>s — the route rides on data-href (anchors
                // keep working via the href fallback).
                const href = target.getAttribute('data-href') || (target as HTMLAnchorElement).getAttribute('href');
                if (href) nav(href, { replace: true });
              }
            }}
          >
            {stack.length > 0
              ? (
                  <ol className="flex items-center gap-1 min-w-0">
                    {stack.map((frame, i) => (
                      <li key={i} className="inline-flex items-center gap-1">
                        <button
                          onClick={() => zoomOut(i, nav)}
                          className="text-primary hover:underline rounded-sm focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                          title={`Back to ${frame.label}`}
                        >
                          {frame.label}
                        </button>
                        <span aria-hidden="true" className="text-muted">›</span>
                      </li>
                    ))}
                    {currentLabel && (
                      <li className="inline-flex items-center">
                        <span aria-current="page" className="text-[0.875rem] font-semibold text-primary-text tracking-tight whitespace-nowrap pl-0.5">
                          {currentLabel}
                        </span>
                      </li>
                    )}
                  </ol>
                )
              : section?.key === 'home'
              ? (
                  <span className="text-[0.8125rem] font-semibold text-primary-text tracking-tight whitespace-nowrap">
                    {greetingText(me?.full_name)}
                    <GreetingExtras />
                  </span>
                )
              : (() => {
                  // The single active tab is computed above (activeParent), also
                  // considering third-level children so the parent stays lit on a
                  // child URL (e.g. Customization while on /admin/automation).
                  if (designing) {
                    return items.map((item) => (
                      <div
                        key={item.to}
                        data-design-to={item.to}
                        role="button"
                        aria-roledescription="draggable tab"
                        aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                        tabIndex={0}
                        aria-label={`${item.label}: press Alt+Left or Alt+Right to move it`}
                        onKeyDown={(e) => {
                          if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
                          e.preventDefault();
                          e.stopPropagation(); // the row's own arrow travel must not run
                          const i = items.findIndex((x) => x.to === item.to);
                          const j = e.key === 'ArrowLeft' ? i - 1 : i + 1;
                          if (j < 0 || j >= items.length) return;
                          moveSub(item.to, items[j].to);
                          const to = item.to;
                          requestAnimationFrame(() => rowRef.current?.querySelector<HTMLElement>(`[data-design-to="${to}"]`)?.focus());
                        }}
                        draggable
                        onDragStart={() => setDragTo(item.to)}
                        onDragOver={(e) => { e.preventDefault(); if (overTo !== item.to) setOverTo(item.to); }}
                        onDragLeave={() => { if (overTo === item.to) setOverTo(null); }}
                        onDrop={(e) => { e.preventDefault(); if (dragTo) moveSub(dragTo, item.to); setDragTo(null); setOverTo(null); }}
                        onDragEnd={() => { setDragTo(null); setOverTo(null); }}
                        title="Drag, or Alt+Left / Alt+Right, to reorder"
                        className={`px-2 py-0.5 rounded-full text-[0.75rem] whitespace-nowrap inline-flex items-center gap-1 border border-dashed cursor-grab active:cursor-grabbing select-none ${
                          dragTo === item.to ? 'opacity-40 border-primary' : overTo === item.to ? 'border-primary ring-2 ring-inset ring-primary bg-primary/10' : 'border-primary/50 bg-primary/[0.04] text-slate-900'
                        }`}
                      >
                        <IconGrip size={10} className="text-muted shrink-0" />
                        {item.label}
                      </div>
                    ));
                  }
                  const pill = (item: typeof items[number]) => {
                    const active = activeParent?.to === item.to;
                    return (
                      <button
                        key={item.to}
                        type="button"
                        data-subtab=""
                        data-href={item.to}
                        data-active={active || undefined}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => nav(item.to)}
                        // NOTE: shadow is a Tailwind CLASS (shadow-sm), not an
                        // inline boxShadow — the focus ring is also a box-shadow,
                        // and an inline boxShadow would override (hide) it.
                        className={pillClass(active)}
                      >
                        {item.label}
                      </button>
                    );
                  };
                  const hidden = items.filter((_, i) => !isShown(i, visibleCount, activeIndex));
                  return (
                    <>
                      {/* Hidden measuring row (all pills at full size + a More
                          sentinel) in its own clip box, like MainNav's. */}
                      <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div ref={pillMeasureRef} className="absolute left-0 top-0 flex w-max items-center gap-[4px] whitespace-nowrap invisible">
                          {items.map((it) => <span key={it.to} data-mi="" className={pillClass(activeParent?.to === it.to)}>{it.label}</span>)}
                          <span data-mi-more="" className={pillClass(false)}>More<IconChevron size={10} /></span>
                        </div>
                      </div>
                      {items.filter((_, i) => isShown(i, visibleCount, activeIndex)).map(pill)}
                      {hidden.length > 0 && (
                        <DropdownMenu
                          align="left"
                          label="More tabs"
                          items={hidden.map((it) => ({ label: it.label, onClick: () => nav(it.to) }))}
                          trigger={<span className={pillClass(false)}>More<IconChevron size={10} /></span>}
                        />
                      )}
                    </>
                  );
                })()}
            {/* The current record / document number now sits at the TAIL of the
                breadcrumb path on the left (kept in its larger 14px font), so
                e.g. "Invoices › INV-001" reads as one path. */}
            <span ref={tailRef} className="inline-flex items-center gap-1">
              {stack.length === 0 && currentLabel && (
                <span className="text-[0.875rem] font-semibold text-primary-text tracking-tight whitespace-nowrap pl-0.5">
                  {currentLabel}
                </span>
              )}
              {/* Label-adjacent affordances (PageLabelActions portal), e.g. the
                  report rename pencil — sits right after the record label. */}
              <span id="subnav-label-slot" className="inline-flex items-center gap-1" />
            </span>
          </nav>
          <div className="justify-self-end inline-flex items-center gap-1.5">
            {/* Action pills portal target. Pages own their button styling
                for use elsewhere in the app; inside this slot we coerce
                only the TRIGGER row (direct children + DropdownMenu's
                wrapper div) to the same 24px (h-[24px]) px-2 size as the table
                toolbar buttons (Views/Export/Columns), so SubNav stays one
                row tall. We deliberately scope to direct/two-deep
                children so dropdown menu contents (rendered via
                `<div className="absolute ...">` siblings of the trigger)
                are NOT squished into 30px rows. */}
            <div
              id="subnav-actions-slot"
              className="inline-flex items-center gap-1.5
                         [&>button]:!h-[24px] [&>button]:!px-2 [&>button]:!text-[0.75rem]
                         [&>button]:!gap-1.5 [&>button]:!leading-none
                         [&>a]:!h-[24px] [&>a]:!px-2 [&>a]:!text-[0.75rem]
                         [&>a]:!gap-1.5 [&>a]:!leading-none
                         [&>div>button]:!h-[24px] [&>div>button]:!px-2
                         [&>div>button]:!text-[0.75rem] [&>div>button]:!gap-1.5
                         [&>div>button]:!leading-none
                         [&>div>button>div]:!h-[24px] [&>div>button>div]:!px-2
                         [&>div>button>div]:!text-[0.75rem] [&>div>button>div]:!gap-1.5
                         [&_svg]:!w-[13px] [&_svg]:!h-[13px]"
            />
            {/* No favorite star on Home — there's no record/page here to star. */}
            {location.pathname !== '/' && <FavoriteStar />}
          </div>
        </div>
      </div>
      {/* Third-level tabs (e.g. Admin Center → Master Data / Customization → its
          items) shown only while inside that area. Standard third-nav style:
          UNDERLINE tabs on the plain canvas (active = colored bottom border +
          dark text), matching in-page tab bars like Ledger → Settings. Filled
          pills are reserved for the second-level row above — never third-level. */}
      {thirdLevel.length > 0 && (
        // No extra left indent: the tabs share the 10px content inset so the
        // first tab lines up with the page's "+" toolbar and tables below.
        <div className="mx-2.5 mt-1 pr-8">
          {/* overflow-y-hidden: overflow-x-auto makes the y axis auto too, and
              each tab's -mb-px underline reaches 1 px below the strip, so a
              vertical scrollbar showed at its end (owner, 2026-10-29: "a
              strange mark like a scroll mark"). Sideways scrolling stays. */}
          <nav
            data-thirdnav
            aria-label={`${activeParent?.label ?? 'Page'} tabs`}
            className="flex items-center gap-1 flex-nowrap min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap border-b border-border-light px-1 -mx-1 pt-1 -mt-1"
            onKeyDown={(e) => {
              // Same arrow travel as the rows above: ←/→ between the tabs,
              // ↑ back to the active second-level pill, ↓ into the page.
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
              const links = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-thirdtab], a[href]'));
              if (links.length === 0) return;
              const idx = links.indexOf(document.activeElement as HTMLElement);
              if (idx < 0) return;
              e.preventDefault();
              if (e.key === 'ArrowUp') {
                const up = document.querySelector<HTMLElement>('[data-subnav] [data-subtab][data-active]')
                  || document.querySelector<HTMLElement>('[data-subnav] [data-subtab]');
                up?.focus();
                return;
              }
              if (e.key === 'ArrowDown') {
                const target =
                  document.querySelector<HTMLElement>('main [role="toolbar"] [tabindex="0"]')
                  || document.querySelector<HTMLElement>('main [role="toolbar"] button:not([disabled]), main [role="toolbar"] a[href], main [role="toolbar"] input:not([type="hidden"]):not([disabled])')
                  || document.querySelector<HTMLElement>('main button:not([disabled]), main a[href], main input:not([type="hidden"]):not([disabled])');
                target?.focus();
                return;
              }
              let next = idx;
              if (e.key === 'ArrowRight') next = Math.min(idx + 1, links.length - 1);
              else if (e.key === 'ArrowLeft') next = Math.max(idx - 1, 0);
              else if (e.key === 'Home') next = 0;
              else if (e.key === 'End') next = links.length - 1;
              const target = links[next];
              if (target) {
                target.focus();
                const href = target.getAttribute('data-href') || (target as HTMLAnchorElement).getAttribute('href');
                if (href && next !== idx) nav(href, { replace: true });
              }
            }}
          >
            {thirdLevel.map((child) => {
              const active = activeChild?.to === child.to;
              return (
                <button
                  key={child.to}
                  type="button"
                  data-thirdtab=""
                  data-href={child.to}
                  data-active={active || undefined}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => nav(child.to)}
                  // ONE highlight device: the brand underline + weight carries
                  // "active"; no box, no fill. The ring is keyboard-only
                  // (focus-visible) and in the brand teal — a mouse click must
                  // never leave a black rectangle behind.
                  className={`px-2.5 py-0.5 text-[0.75rem] tracking-tight whitespace-nowrap border-b-2 -mb-px rounded-t-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                    active
                      ? 'border-primary text-primary-text font-semibold'
                      : 'border-transparent text-secondary font-medium hover:text-primary-text hover:bg-slate-50/70'
                  }`}
                >
                  {child.label}
                </button>
              );
            })}
          </nav>
        </div>
      )}
    </div>
  );
}
